import type { Plan, SerializedCatalogEntry } from '@cfcg/shared';
import { useQuery } from '@tanstack/react-query';
import { Alert, Card } from '../components/ui.js';
import { api } from '../lib/api.js';
import { messageOf } from '../lib/auth.js';

type CatalogResponse = {
  entries: SerializedCatalogEntry[];
  limits: { customRulesPerPlan: Record<Plan, number>; regexMinPlan: Plan };
};

/**
 * Podgląd katalogu ustawień. Kreator presetu z podglądem na żywo powstaje
 * w Etapie 6 — na razie potwierdzamy, że katalog dojeżdża do frontu razem
 * z gotowymi polskimi opisami.
 */
export function CatalogPage() {
  const { data, error, isPending } = useQuery({
    queryKey: ['catalog'],
    queryFn: () => api.get<CatalogResponse>('/api/admin/catalog'),
  });

  return (
    <div className="space-y-6">
      <Alert tone="info">
        Etap 1 — podgląd katalogu. Kreator presetu i historia zastosowań powstaną w kolejnych
        etapach.
      </Alert>

      {isPending && <p className="text-sm text-neutral-500">Wczytywanie katalogu…</p>}
      {error && <Alert tone="error">{messageOf(error)}</Alert>}

      {data && (
        <Card title={`Ustawienia (${data.entries.length})`}>
          <ul className="space-y-3">
            {data.entries.map((entry) => (
              <li key={entry.key} className="rounded border border-neutral-200 p-3">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-medium text-neutral-900">{entry.label}</span>
                  <code className="text-xs text-neutral-500">{entry.kind}</code>
                </div>
                <p className="mt-1 text-sm text-neutral-600">{entry.hint}</p>

                {entry.deprecated && (
                  <p className="mt-2 rounded bg-amber-50 p-2 text-xs text-amber-900">
                    {entry.deprecated.sincePl}
                  </p>
                )}

                <ul className="mt-2 space-y-1">
                  {entry.values.map((value) => (
                    <li key={String(value.value)} className="text-xs text-neutral-500">
                      <code className="text-neutral-700">{String(value.value)}</code> —{' '}
                      {value.changePl}
                      {value.note && (
                        <span
                          className={
                            value.note.severity === 'blocking'
                              ? 'ml-1 text-red-700'
                              : value.note.severity === 'warn'
                                ? 'ml-1 text-amber-700'
                                : 'ml-1 text-neutral-500'
                          }
                        >
                          ({value.note.textPl})
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
